// components/auth/AuthDialog.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    CircularProgress,
    Link,
    Typography,
    MenuItem,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import { useAuth } from '../../hooks/auth/useAuth';
import { loginPromptManager, LoginPromptType } from '../../utils/auth/loginPromptManager';
import logger from '../../utils/logging/logger';
import ForgotPasswordDialog from './ForgotPasswordDialog';
import ForcePasswordChangeDialog from './ForcePasswordChangeDialog';
import config from '../../config';
import sessionManager from '../../utils/session/sessionManager';

const componentName = 'AuthDialog';

// Compact text field styling
const compactFieldSx = {
    '& .MuiOutlinedInput-root': {
        height: '45px',
    },
    '& .MuiSelect-select': {
        height: '45px',
        display: 'flex',
        alignItems: 'center',
    },
};

// US States list
const US_STATES = [
    { code: 'AL', name: 'Alabama' },
    { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' },
    { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' },
    { code: 'DE', name: 'Delaware' },
    { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' },
    { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' },
    { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' },
    { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' },
    { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' },
    { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' },
    { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' },
    { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' },
    { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' },
    { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' },
    { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' },
    { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' },
    { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' },
    { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' },
    { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' },
    { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' },
    { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' },
    { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' },
    { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' },
    { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' },
    { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
    { code: 'DC', name: 'District of Columbia' }
];

export interface AuthDialogProps {
    open: boolean;
    onClose: () => void;
    defaultPromptType?: LoginPromptType;
    onSuccess?: () => void;
    // Pre-filled values for registration
    prefillEmail?: string;
    prefillFirstName?: string;
    prefillLastName?: string;
}

export const AuthDialog: React.FC<AuthDialogProps> = ({
    open,
    onClose,
    defaultPromptType = LoginPromptType.SIGN_IN,
    onSuccess,
    prefillEmail = '',
    prefillFirstName = '',
    prefillLastName = ''
}) => {
    const {
        user,
        isAnonymous,
        isAuthenticated,
        login,
        logout,
        convertAnonymousAccount,
        refreshUserInfo,
        saveUserInfoToCookie,
        createAnonymousAccount
    } = useAuth();

    const preventPageReload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
    };

    const [hasInitialized, setHasInitialized] = useState(false);

    // Form state
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [userName, setUsername] = useState('');
    const [userNameOrEmail, setUsernameOrEmail] = useState('');
    const [email, setEmail] = useState('');
    
    // Address fields
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [country, setCountry] = useState('USA');
    const [isInternational, setIsInternational] = useState(false);
    const [address1, setAddress1] = useState('');
    const [address2, setAddress2] = useState('');
    
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);
    const [forcePasswordChangeUser, setForcePasswordChangeUser] = useState<{userId: number; userName: string} | null>(null);

    // UI state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [promptType, setPromptType] = useState<LoginPromptType>(defaultPromptType);

    // Ref to scroll to error message
    const dialogContentRef = useRef<HTMLDivElement>(null);

    // Scroll to top when error is set so user can see it
    useEffect(() => {
        if (error && dialogContentRef.current) {
            dialogContentRef.current.scrollTop = 0;
        }
    }, [error]);

    // Effect to determine correct prompt type based on auth state
    useEffect(() => {
        if (open) {
            if (defaultPromptType) {
                setPromptType(defaultPromptType);
                loginPromptManager.recordPromptShown(defaultPromptType);
            } else {
                const fallbackType = loginPromptManager.getAppropriatePromptType(
                    isAnonymous,
                    isAuthenticated
                );
                setPromptType(fallbackType);
                loginPromptManager.recordPromptShown(fallbackType);
            }
        }
    }, [open, defaultPromptType]);

    // SECURITY: Clear password fields when switching to create account mode or when dialog opens
    useEffect(() => {
        if (open && (promptType === LoginPromptType.CREATE_ACCOUNT || promptType === LoginPromptType.CONVERT_ANONYMOUS)) {
            setPassword('');
            setConfirmPassword('');
        }
    }, [open, promptType]);

    useEffect(() => {
        if (open && !hasInitialized) {
            // Only reset on first open
            setError('');
            setIsSubmitting(false);
            setHasInitialized(true);

            // Set initial values - use prefill values if provided
            if (!password) setPassword('');
            if (!confirmPassword) setConfirmPassword('');
            if (!userNameOrEmail) {
                setUsernameOrEmail(
                    user.userName_DisplayLogin || (user.isAnonymous ? '' : (user.userName || ''))
                );
            }

            if (!userName && isAnonymous) {
                setUsername('');
            }

            // Pre-fill email, firstName, lastName if provided (e.g., from measurement form)
            if (prefillEmail) {
                setEmail(prefillEmail);
            }
            if (prefillFirstName) {
                setFirstName(prefillFirstName);
            }
            if (prefillLastName) {
                setLastName(prefillLastName);
            }

            // Reset address fields
            setCity('');
            setState('');
            setZipCode('');
            setCountry('USA');
            setIsInternational(false);
            setAddress1('');
            setAddress2('');
        } else if (!open) {
            // Reset initialization flag when dialog closes
            setHasInitialized(false);
            // Also reset prefill-related fields when dialog closes
            setEmail('');
            setFirstName('');
            setLastName('');
        }
    }, [open, hasInitialized, password, userNameOrEmail, userName, isAnonymous, user.userName_DisplayLogin, user.userName, prefillEmail, prefillFirstName, prefillLastName]);

    const validateRequiredFields = (
        fields: Record<string, string>,
        fieldLabels: Record<string, string>
    ): string | null => {
        for (const [key, value] of Object.entries(fields)) {
            if (!value.trim()) {
                return `Please enter your ${fieldLabels[key] || key}`;
            }
        }
        return null;
    };

    // Handle international checkbox change
    const handleInternationalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        setIsInternational(checked);
        if (checked) {
            setState(''); // Clear state selection for international
            setCountry(''); // Clear country to let user enter manually
        } else {
            setCountry('USA'); // Reset to USA
        }
    };

    // Determine if we should show the new account form
    const isNewAccountForm = promptType === LoginPromptType.CREATE_ACCOUNT ||
        promptType === LoginPromptType.CONVERT_ANONYMOUS;

    // Handle toggling between sign in and create account
    const handleToggleForm = () => {
        const newPromptType = promptType === LoginPromptType.SIGN_IN
            ? LoginPromptType.CREATE_ACCOUNT
            : LoginPromptType.SIGN_IN;
        
        setPromptType(newPromptType);

        // Clear errors when switching forms
        setError('');
        
        // SECURITY: Clear password fields when switching to create account
        if (newPromptType === LoginPromptType.CREATE_ACCOUNT) {
            setPassword('');
            setConfirmPassword('');
        }
    };

    const handleForgotPasswordClick = () => {
        setShowForgotPassword(true);
    };

    const handleForgotPasswordClose = () => {
        setShowForgotPassword(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !isSubmitting) {
            e.preventDefault();
            handleSubmit();
        }
    };

    // Add this function before your return statement in the component
    const handleContinueAsGuest = async () => {
        setIsSubmitting(true);
        try {
            // If not already anonymous, create a new anonymous account
            if (!isAnonymous) {
                const newAnonUser = await createAnonymousAccount();
                // Force save to ensure it overrides any existing cookie
                saveUserInfoToCookie(newAnonUser, true);
                // Refresh auth state
                await refreshUserInfo();
            } else {
                // Already anonymous, just force save to ensure cookie is updated
                saveUserInfoToCookie(user, true);
            }

            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error("Failed to set up guest account:", error);
            setError("Failed to continue as guest. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle form submission
    const handleSubmit = async () => {
        setError('');
        setIsSubmitting(true);

        // Add event listener to prevent page reload
        window.addEventListener('beforeunload', preventPageReload);

        try {
            
            if (promptType === LoginPromptType.SIGN_IN) {
                // Validate required fields for sign in
                const signInError = validateRequiredFields(
                    { userNameOrEmail, password },
                    { userNameOrEmail: 'user name', password: 'password' }
                );
                if (signInError) {
                    setError(signInError);
                    return;
                }

                try {
                    // IMPORTANT: Capture anonymous user ID BEFORE login (it will be cleared after)
                    // Check both sessionManager storage AND current user state
                    const anonymousUserData = sessionManager.retrieveAnonymousUser();
                    const currentUserId = user.userId;

                    // Use sessionManager's stored anonymous userId if available, otherwise fall back to current user
                    const anonymousUserId = anonymousUserData?.userId || currentUserId;

                    // Consider user "was anonymous" if:
                    // 1. sessionManager has anonymous user data stored, OR
                    // 2. current user is marked as anonymous with a valid userId
                    const wasAnonymous = (anonymousUserData !== null) || (user.isAnonymous && currentUserId > 0);

                    logger.log('LogAuth', componentName, 'Captured anonymous user before login', {
                        anonymousUserId,
                        wasAnonymous,
                        currentUserId,
                        hasStoredAnonymousData: anonymousUserData !== null,
                        userIsAnonymous: user.isAnonymous
                    });

                    // Use AuthenticationManager's login
                    const loggedInUser = await login(userNameOrEmail, password);

                    // Login succeeded - ONLY NOW remove the prevention and proceed
                    window.removeEventListener('beforeunload', preventPageReload);

                    // Check if user must change password before continuing
                    if (loggedInUser.mustChangePassword) {
                        logger.log('LogAuth', componentName, 'User must change password before continuing', {
                            userId: loggedInUser.userId,
                            userName: loggedInUser.userName
                        });
                        setForcePasswordChangeUser({
                            userId: loggedInUser.userId,
                            userName: loggedInUser.userName
                        });
                        setShowForcePasswordChange(true);
                        setIsSubmitting(false);
                        return; // Don't proceed with normal login flow
                    }

                    // If the user was anonymous and had active orders, reassign them to the logged-in user
                    if (wasAnonymous && anonymousUserId !== loggedInUser.userId) {
                        try {
                            logger.log('LogAuth', componentName, 'Checking for active orders to reassign', {
                                fromUserId: anonymousUserId,
                                toUserId: loggedInUser.userId
                            });

                            // Get active orders for the anonymous user
                            const ordersResponse = await fetch(
                                `${config.API_URL}/api/order-group/${anonymousUserId}`,
                                {
                                    method: 'GET',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'X-User-Id': loggedInUser.userId.toString()
                                    }
                                }
                            );

                            if (ordersResponse.ok) {
                                const orders = await ordersResponse.json();

                                // Get unique OrderIds from the cart items
                                const uniqueOrderIds = [...new Set(orders.map((item: { OrderId: number }) => item.OrderId))];

                                logger.log('LogAuth', componentName, 'Found orders to reassign', {
                                    orderCount: uniqueOrderIds.length,
                                    orderIds: uniqueOrderIds
                                });

                                // Reassign each order to the logged-in user
                                for (const orderId of uniqueOrderIds) {
                                    try {
                                        const reassignResponse = await fetch(
                                            `${config.API_URL}/api/order/${orderId}/reassign-user`,
                                            {
                                                method: 'POST',
                                                headers: {
                                                    'Content-Type': 'application/json',
                                                    'X-User-Id': loggedInUser.userId.toString()
                                                },
                                                body: JSON.stringify({
                                                    fromUserId: anonymousUserId,
                                                    toUserId: loggedInUser.userId
                                                })
                                            }
                                        );

                                        if (reassignResponse.ok) {
                                            logger.log('LogAuth', componentName, 'Order reassigned successfully', {
                                                orderId,
                                                toUserId: loggedInUser.userId
                                            });
                                        } else {
                                            logger.log('LogAuth', componentName, 'Order reassignment failed', {
                                                orderId,
                                                status: reassignResponse.status
                                            });
                                        }
                                    } catch (reassignError) {
                                        logger.log('LogError', componentName, 'Error reassigning order', {
                                            orderId,
                                            error: reassignError
                                        });
                                    }
                                }
                            }
                        } catch (orderError) {
                            // Don't block login if order reassignment fails
                            logger.log('LogError', componentName, 'Error during order reassignment', orderError);
                        }
                    }

                    // Set success message and refresh for consistent state
                    setSuccess('Login successful! Refreshing page...');

                    // Clear auth cache to ensure fresh state
                    sessionManager.clearData(config.LAST_ENSURE_USER_KEY);
                    await refreshUserInfo();

                    // Brief delay then refresh
                    setTimeout(() => {
                        if (onSuccess) onSuccess();
                        onClose();
                        window.location.reload();
                    }, 2000);
                } catch (loginErr) {
                    console.error('Login failed:', loginErr);

                    // Extract meaningful error message
                    let errorMessage = 'Authentication failed';
                    if (loginErr instanceof Error) {
                        if (loginErr.message.includes('Invalid credentials') ||
                            loginErr.message.includes('Authentication failed')) {
                            errorMessage = 'Invalid username/email or password. Please try again.';
                        } else if (loginErr.message.includes('User not found')) {
                            errorMessage = 'Account not found. Please check your username/email.';
                        } else {
                            errorMessage = loginErr.message;
                        }
                    }

                    setError(errorMessage);
                    return;
                }
            } else if (
                promptType === LoginPromptType.CONVERT_ANONYMOUS ||
                promptType === LoginPromptType.CREATE_ACCOUNT
            ) {
                // Validate required fields for account conversion (address fields are optional)
                const requiredFields: Record<string, string> = {
                    userName: userName,
                    email: email,
                    password: password,
                    firstName: firstName,
                    lastName: lastName
                };

                const fieldLabels: Record<string, string> = {
                    userName: 'user name',
                    email: 'email address',
                    password: 'password',
                    firstName: 'first name',
                    lastName: 'last name'
                };

                const missingFieldError = validateRequiredFields(requiredFields, fieldLabels);
                if (missingFieldError) {
                    setError(missingFieldError);
                    return;
                }

                // Validate password confirmation for new accounts
                if (password !== confirmPassword) {
                    setError('Passwords do not match');
                    return;
                }

                if (password.length < 8) {
                    setError('Password must be at least 8 characters long');
                    return;
                }

                // Check for number or special character
                const hasNumberOrSpecial = /[\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
                if (!hasNumberOrSpecial) {
                    setError('Password must contain at least one number or special character');
                    return;
                }

                // Use the provided userName or fall back to the email address
                const finalUsername = userName || email;
                logger.log('LogAuth', componentName, 'convertAnonymousAccount Attempting to convert anonymous account', {
                    userId: user.userId,
                    userName: finalUsername
                });

                logger.log('LogAuth', componentName, 'convertAnonymousAccount - User object before conversion:', user);
                logger.log('LogAuth', componentName, 'convertAnonymousAccount - userId value:', user.userId);

                // Call convertAnonymousAccount with address information
                const success = await convertAnonymousAccount(
                    user.userId,
                    finalUsername,
                    email,
                    firstName,
                    lastName,
                    password,
                    // Address fields
                    city,
                    state,
                    zipCode,
                    isInternational ? (country || '') : 'USA',
                    address1,
                    address2
                );

                if (success) {
                    logger.log('LogAuth', componentName, 'convertAnonymousAccount - success userId value:', user.userId);

                    // Show success immediately
                    setSuccess('Account created successfully! Refreshing page...');

                    // Clear auth cache
                    sessionManager.clearData(config.LAST_ENSURE_USER_KEY);
                    await refreshUserInfo();

                    // Brief delay to show success message, then refresh
                    setTimeout(() => {
                        if (onSuccess) onSuccess();
                        onClose();

                        // Force page refresh to ensure all components get fresh auth state
                        window.location.reload();
                    }, 1500); // 1.5 seconds to see the success message

                } else {
                    logger.logError('Error', componentName, 'Error - convertAnonymousAccount Failed to create account. userId value:', user.userId);
                    setError('Failed to create account. Please try again.');
                }

            }
        }
        catch (err) {
            console.error('Auth error:', err);
            setError(err instanceof Error ? err.message : 'Authentication failed');
        } finally {
            // Remove the event listener in case it wasn't removed earlier
            window.removeEventListener('beforeunload', preventPageReload);
            setIsSubmitting(false);
        }
    };

    // Get the appropriate dialog title based on prompt type
    const getDialogTitle = () => {
        switch (promptType) {
            case LoginPromptType.CONVERT_ANONYMOUS:
                return "Complete Your Account";
            case LoginPromptType.CREATE_ACCOUNT:
                return "Create an Account - save your quotes";
            case LoginPromptType.SIGN_IN:
                return "Sign In";
            default:
                return "Account";
        }
    };

    // Get the appropriate dialog message based on prompt type
    const getDialogMessage = () => {
        switch (promptType) {
            case LoginPromptType.CONVERT_ANONYMOUS:
                return "Add your information to save your quotes and receive updates.";
            case LoginPromptType.CREATE_ACCOUNT:
                return "";
            case LoginPromptType.SIGN_IN:
                return "Sign in to access your saved quotes and preferences.";
            default:
                return "";
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                aria-labelledby="auth-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="auth-dialog-title">
                    {getDialogTitle()}
                </DialogTitle>

                <DialogContent ref={dialogContentRef}>
                    <DialogContentText>
                        {getDialogMessage()}
                    </DialogContentText>

                    {error && (
                        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    <Box sx={{ mt: 2 }}>
                        {isNewAccountForm && (
                            <>
                                <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                    <TextField
                                        autoComplete="given-name"
                                        margin="none"
                                        label="First Name"
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                        onKeyDown={handleKeyDown}
                                        sx={compactFieldSx}
                                    />
                                    <TextField
                                        autoComplete="family-name"
                                        margin="none"
                                        label="Last Name"
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                        onKeyDown={handleKeyDown}
                                        sx={compactFieldSx}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                    <TextField
                                        autoComplete="username"
                                        margin="none"
                                        label="Username or Email"
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        value={userName}
                                        onChange={(e) => setUsername(e.target.value)}
                                        size="small"
                                        sx={compactFieldSx}
                                        disabled={isSubmitting}
                                        onKeyDown={handleKeyDown}
                                    />
                                    <TextField
                                        autoComplete="email"
                                        margin="none"
                                        label="Email Address"
                                        type="email"
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        sx={compactFieldSx}
                                        required
                                        disabled={isSubmitting}
                                        onKeyDown={handleKeyDown}
                                    />
                                </Box>
                            </>
                        )}
                        {promptType === LoginPromptType.SIGN_IN && (
                            <TextField
                                autoComplete="username"
                                autoFocus={!isNewAccountForm}
                                margin="none"
                                label="Username"
                                type="text"
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={userNameOrEmail}
                                onChange={(e) => setUsernameOrEmail(e.target.value)}
                                sx={{ ...compactFieldSx, mb: 1 }}
                                required
                                disabled={isSubmitting}
                                onKeyDown={handleKeyDown}
                            />
                        )}

                        {/* Address Fields for New Accounts */}
                        {isNewAccountForm && (
                            <>
                                <TextField
                                    autoComplete="address-line1"
                                    margin="none"
                                    label="Street Address (Optional)"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={address1}
                                    onChange={(e) => setAddress1(e.target.value)}
                                    disabled={isSubmitting}
                                    onKeyDown={handleKeyDown}
                                    sx={{ ...compactFieldSx, mb: 1 }}
                                />
                                
                                <TextField
                                    autoComplete="address-line2"
                                    margin="none"
                                    label="Apartment, Suite, etc. (Optional)"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={address2}
                                    onChange={(e) => setAddress2(e.target.value)}
                                    disabled={isSubmitting}
                                    onKeyDown={handleKeyDown}
                                    sx={{ ...compactFieldSx, mb: 1 }}
                                />
                                
                                <TextField
                                    autoComplete="address-level2"
                                    margin="none"
                                    label="City (Optional)"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    disabled={isSubmitting}
                                    onKeyDown={handleKeyDown}
                                    sx={{ ...compactFieldSx, mb: 1 }}
                                />

                                <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                    {!isInternational ? (
                                        <TextField
                                            select
                                            margin="none"
                                            label="State (Optional)"
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            value={state}
                                            onChange={(e) => setState(e.target.value)}
                                            disabled={isSubmitting}
                                            sx={compactFieldSx}
                                        >
                                            <MenuItem value="">
                                                <em>Select State</em>
                                            </MenuItem>
                                            {US_STATES.map((stateOption) => (
                                                <MenuItem key={stateOption.code} value={stateOption.code}>
                                                    {stateOption.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    ) : (
                                        <TextField
                                            autoComplete="address-level1"
                                            margin="none"
                                            label="State/Province (Optional)"
                                            type="text"
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            value={state}
                                            onChange={(e) => setState(e.target.value)}
                                            disabled={isSubmitting}
                                            onKeyDown={handleKeyDown}
                                            sx={compactFieldSx}
                                        />
                                    )}
                                    
                                    <TextField
                                        autoComplete="postal-code"
                                        margin="none"
                                        label={isInternational ? "Postal Code (Optional)" : "ZIP Code (Optional)"}
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        value={zipCode}
                                        onChange={(e) => setZipCode(e.target.value)}
                                        disabled={isSubmitting}
                                        onKeyDown={handleKeyDown}
                                        sx={compactFieldSx}
                                    />
                                </Box>

                                <Box sx={{ mb: 1 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isInternational}
                                                onChange={handleInternationalChange}
                                                disabled={isSubmitting}
                                            />
                                        }
                                        label="International address (outside US)"
                                    />
                                </Box>

                                {isInternational && (
                                    <TextField
                                        autoComplete="country"
                                        margin="none"
                                        label="Country"
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        value={country}
                                        onChange={(e) => setCountry(e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                        onKeyDown={handleKeyDown}
                                        sx={{ ...compactFieldSx, mb: 1 }}
                                    />
                                )}
                            </>
                        )}

                        {isNewAccountForm && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 1 }}>
                                <Typography variant="body2" color="textSecondary">
                                    Requirements: 8+ characters, include a number or special character
                                </Typography>
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={handleForgotPasswordClick}
                                    underline="hover"
                                >
                                    Forgot password?
                                </Link>
                            </Box>
                        )}
                        {isNewAccountForm ? (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    autoComplete="new-password"
                                    margin="none"
                                    label="Password"
                                    type="password"
                                    fullWidth
                                    variant="outlined"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    size="small"
                                    disabled={isSubmitting}
                                    onKeyDown={handleKeyDown}
                                    sx={compactFieldSx}
                                />
                                <TextField
                                    autoComplete="new-password"
                                    margin="none"
                                    label="Confirm Password"
                                    type="password"
                                    fullWidth
                                    variant="outlined"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    size="small"
                                    disabled={isSubmitting}
                                    onKeyDown={handleKeyDown}
                                    sx={compactFieldSx}
                                />
                            </Box>
                        ) : (
                            <TextField
                                autoComplete="new-password"
                                margin="none"
                                label="Password"
                                type="password"
                                fullWidth
                                variant="outlined"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                size="small"
                                disabled={isSubmitting}
                                onKeyDown={handleKeyDown}
                                sx={{ ...compactFieldSx, mb: 1 }}
                            />
                        )}
                    </Box>
                    {!isNewAccountForm && (
                        <Box textAlign="right" mt={1}>
                            <Link
                                component="button"
                                variant="body2"
                                onClick={handleForgotPasswordClick}
                                underline="hover"
                            >
                                Forgot password?
                            </Link>
                        </Box>
                    )}
                    {promptType !== LoginPromptType.CONVERT_ANONYMOUS && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Link
                                component="button"
                                variant="body2"
                                onClick={() => window.open('/sail-ordering-process', '_blank')}
                                underline="hover"
                                sx={{ mr: 2 }}
                            >
                                How does ordering work?
                            </Link>
                            <Button
                                onClick={handleToggleForm}
                                disabled={isSubmitting}
                            >
                                {promptType === LoginPromptType.SIGN_IN
                                    ? "Need to create an account?"
                                    : "Already have an account?"}
                            </Button>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={handleContinueAsGuest}
                        color="primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Continue as Guest"}
                    </Button>

                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            promptType === LoginPromptType.CONVERT_ANONYMOUS
                                ? "Complete Account"
                                : (promptType === LoginPromptType.CREATE_ACCOUNT ? "Create Account" : "Sign In")
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
            <ForgotPasswordDialog
                open={showForgotPassword}
                onClose={handleForgotPasswordClose}
            />
            {forcePasswordChangeUser && (
                <ForcePasswordChangeDialog
                    open={showForcePasswordChange}
                    userId={forcePasswordChangeUser.userId}
                    userName={forcePasswordChangeUser.userName}
                    onLogout={() => {
                        logger.log('LogAuth', componentName, 'User chose to logout from force password change dialog', {
                            userId: forcePasswordChangeUser.userId
                        });
                        setShowForcePasswordChange(false);
                        setForcePasswordChangeUser(null);
                        logout();
                        onClose();
                    }}
                    onPasswordChanged={async () => {
                        logger.log('LogAuth', componentName, 'Force password change completed', {
                            userId: forcePasswordChangeUser.userId
                        });
                        setShowForcePasswordChange(false);
                        setForcePasswordChangeUser(null);

                        // Now complete the login flow
                        setSuccess('Password changed successfully! Refreshing page...');

                        // Clear auth cache to ensure fresh state
                        sessionManager.clearData(config.LAST_ENSURE_USER_KEY);
                        await refreshUserInfo();

                        // Brief delay then refresh
                        setTimeout(() => {
                            if (onSuccess) onSuccess();
                            onClose();
                            window.location.reload();
                        }, 2000);
                    }}
                />
            )}
        </>
    );
};

export default AuthDialog;